import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileText, Pencil, TrendingUp, UserCog, Layers, Printer } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { formatCurrency, API_URL } from "../../lib/utils";
import { printPayslip, printPayslipsBatch, monthLabel } from "../../lib/payslipPrint";
import { SalaryAdjustDialog } from "./SalaryAdjustDialog";
import { ProcessPayrollDialog } from "./ProcessPayrollDialog";
import { EmployeeProfileDialog } from "./EmployeeProfileDialog";

export const PayrollTab = ({ payroll, loading, month, headers, settings, onRefresh }) => {
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const openDialog = (emp, which) => { setSelected(emp); setDialog(which); };

  const unprocessed = payroll.filter((e) => !e.payroll && (e.base_salary || 0) > 0);
  const processed = payroll.filter((e) => e.payroll);

  const processAll = async () => {
    if (!window.confirm(`Processar o vencimento de ${unprocessed.length} funcionário(s) de ${monthLabel(month)} com o salário base, desconto INSS 3% e os descontos de faltas/atrasos do mês? Pode editar cada recibo depois.`)) return;
    setBatchBusy(true);
    try {
      const r = await axios.post(`${API_URL}/hr/payroll/batch`, { month, apply_inss: true, include_attendance: true }, { headers, withCredentials: true });
      const skipped = r.data.skipped.filter((s) => s.reason === "sem salário base definido");
      toast.success(`${r.data.processed.length} recibo(s) gerado(s)${skipped.length ? ` · ${skipped.length} sem salário base` : ""}`);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no processamento em lote"); }
    finally { setBatchBusy(false); }
  };

  const printAll = () => printPayslipsBatch({
    records: processed.map((e) => e.payroll), settings,
    employeesById: Object.fromEntries(processed.map((e) => [e.user_id, e])),
  });

  const pay = async (emp) => {
    if (!window.confirm(`Pagar vencimento de ${emp.name} (${formatCurrency(emp.payroll.net_salary)})? Será registado como despesa no Financeiro.`)) return;
    try {
      await axios.put(`${API_URL}/hr/payroll/${emp.payroll.payroll_id}/pay`, {}, { headers, withCredentials: true });
      toast.success("Vencimento pago e registado no Financeiro");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const totals = payroll.reduce((a, e) => { if (e.payroll) { a.gross += e.payroll.gross_salary ?? e.payroll.base_salary + (e.payroll.bonus || 0); a.net += e.payroll.net_salary; a.n += 1; } return a; }, { gross: 0, net: 0, n: 0 });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{monthLabel(month)} · {processed.length}/{payroll.length} processados</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={processAll} disabled={batchBusy || unprocessed.length === 0} data-testid="payroll-process-all-btn">
            <Layers className="w-4 h-4 mr-1" /> {batchBusy ? "A processar…" : `Processar todos (${unprocessed.length})`}
          </Button>
          <Button size="sm" onClick={printAll} disabled={processed.length === 0} data-testid="payroll-print-all-btn">
            <Printer className="w-4 h-4 mr-1" /> Imprimir todos os recibos ({processed.length})
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm" data-testid="payroll-summary">
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Processados</p><p className="font-semibold">{totals.n} / {payroll.length}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Bruto total</p><p className="font-mono font-semibold">{formatCurrency(totals.gross)}</p></CardContent></Card>
        <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Líquido total</p><p className="font-mono font-semibold">{formatCurrency(totals.net)}</p></CardContent></Card>
      </div>
      <Card><CardContent className="p-0 divide-y">
        {loading ? <p className="p-6 text-muted-foreground">A carregar...</p> : payroll.map((emp) => (
          <div key={emp.user_id} className="flex items-center justify-between p-3 gap-3 flex-wrap" data-testid={`payroll-row-${emp.user_id}`}>
            <div className="flex-1 min-w-40">
              <p className="font-medium">{emp.full_name || emp.name}</p>
              <p className="text-xs text-muted-foreground">{emp.position || <span className="capitalize">{emp.role}</span>}{emp.inss_number ? ` · INSS ${emp.inss_number}` : ""}</p>
              {!emp.inss_number && <p className="text-[11px] text-amber-600" data-testid={`inss-missing-${emp.user_id}`}>Sem Nº INSS</p>}
              {emp.attendance && (emp.attendance.absences > 0 || emp.attendance.late_count > 0) && emp.payroll?.status !== "paid" && (
                <p className="text-[11px] text-red-600" data-testid={`attendance-note-${emp.user_id}`}>
                  {emp.attendance.absences} falta(s) · {emp.attendance.late_count} atraso(s) → −{formatCurrency(emp.attendance.deduction_total)}
                  {emp.payroll && !(emp.payroll.deductions_items || []).some((d) => /^(Faltas|Atrasos) \(/.test(d.label)) && <span className="text-muted-foreground"> (não incluído no recibo — edite para aplicar)</span>}
                </p>
              )}
            </div>
            <div className="text-sm"><p className="text-xs text-muted-foreground">Salário base</p><p className="font-mono">{formatCurrency(emp.base_salary)}</p></div>
            <div className="text-sm min-w-28">
              {emp.payroll ? <><p className="text-xs text-muted-foreground">Líquido · {emp.payroll.receipt_number || emp.payroll.month}</p><p className="font-mono font-bold">{formatCurrency(emp.payroll.net_salary)}</p></> : <Badge variant="outline">Não processado</Badge>}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {emp.payroll?.status === "paid" ? <Badge className="bg-emerald-500/20 text-emerald-600">Pago</Badge>
                : emp.payroll ? <>
                  <Button size="sm" onClick={() => pay(emp)} data-testid={`pay-payroll-${emp.user_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Pagar</Button>
                  <Button size="sm" variant="ghost" onClick={() => openDialog(emp, "process")} title="Editar vencimento" data-testid={`edit-payroll-${emp.user_id}`}><Pencil className="w-4 h-4" /></Button>
                </> : <Button size="sm" variant="outline" onClick={() => openDialog(emp, "process")} data-testid={`process-payroll-${emp.user_id}`}>Processar</Button>}
              {emp.payroll && <Button size="sm" variant="outline" onClick={() => printPayslip({ record: emp.payroll, settings, employee: emp })} data-testid={`print-payslip-${emp.user_id}`}><FileText className="w-4 h-4 mr-1" /> Recibo</Button>}
              <Button size="icon" variant="ghost" title="Ajustar salário" onClick={() => openDialog(emp, "salary")} data-testid={`edit-salary-${emp.user_id}`}><TrendingUp className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" title="Dados RH" onClick={() => openDialog(emp, "profile")} data-testid={`hr-profile-${emp.user_id}`}><UserCog className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </CardContent></Card>
      <SalaryAdjustDialog open={dialog === "salary"} onOpenChange={(o) => !o && setDialog(null)} employee={selected} headers={headers} onSaved={onRefresh} />
      <ProcessPayrollDialog open={dialog === "process"} onOpenChange={(o) => !o && setDialog(null)} employee={selected} month={month} headers={headers} onSaved={onRefresh} />
      <EmployeeProfileDialog open={dialog === "profile"} onOpenChange={(o) => !o && setDialog(null)} employee={selected} headers={headers} onSaved={onRefresh} />
    </div>
  );
};
